import { useRequestURL, useRoute } from '#imports';
import { normalizeDateToISOString } from './dateFormatter';
import { getRouteLanguage } from './language-translation';
import { removeEmptyFields, normalizeKeywords } from './json-ld-utils';
import { STRUCTURED_DATA_DEFAULTS } from './structuredDataDefaults';
import type {
  ArticleLikeStructuredDataOptions,
  CommerceLikeStructuredDataOptions,
  CreativeWorkStructuredDataOptions,
  EventStructuredDataOptions,
  NameDescriptionOptions,
  OrgLikeStructuredDataOptions,
  StructuredDataOptions,
  StructuredDataSchemaType,
} from './structuredDataTypes';
import type { PlanRefQueryResult } from '~/sanity/types';

function resolveCanonicalUrl(options: StructuredDataOptions): string {
  // Prefer explicit caller-provided URL values so page-level callers can override defaults.
  if (options.canonicalUrl || options.pageUrl) {
    return options.canonicalUrl || options.pageUrl || '';
  }

  if (import.meta.server) {
    const requestUrl = useRequestURL();
    return requestUrl?.toString() || '';
  }

  /** In client-side context, use the current window location
  as the canonical URL when not explicitly provided. */
  if (import.meta.client) {
    return window.location.href;
  }

  return '';
}

function resolveLanguage(options: StructuredDataOptions): string {
  // Route language is the canonical fallback so emitted schema language matches page locale.
  return options.lang || getRouteLanguage(useRoute().params.lang as string | string[] | undefined);
}

function resolveImageUrl(options: StructuredDataOptions): string {
  // Keep explicit imageUrl highest priority; otherwise derive from a Sanity asset ref.
  return options.imageUrl || (options.imageRef ? cdnImage(options.imageRef).asIs : '');
}

type StructuredDataResolvedContext = {
  canonicalUrl: string;
  language: string;
  imageUrl: string;
};

function buildResolvedContext(options: StructuredDataOptions): StructuredDataResolvedContext {
  // Consolidate shared context once to keep schema handlers deterministic.
  return {
    canonicalUrl: resolveCanonicalUrl(options),
    language: resolveLanguage(options),
    imageUrl: resolveImageUrl(options),
  };
}

function resolveSiteOrigin(canonicalUrl: string): string | undefined {
  try {
    return new URL(canonicalUrl).origin;
  } catch {
    // Callers already guard canonical URL, but parsing can still fail for malformed values.
    return;
  }
}

function buildSchemaBaseFields({
  context,
  schemaType,
  idSuffix,
  includeLanguage = true,
}: {
  context: StructuredDataResolvedContext;
  schemaType: StructuredDataSchemaType;
  idSuffix: string;
  includeLanguage?: boolean;
}) {
  return {
    '@context': 'https://schema.org',
    '@type': schemaType,
    '@id': `${context.canonicalUrl}#${idSuffix}`,
    url: context.canonicalUrl,
    inLanguage: includeLanguage ? context.language : undefined,
  };
}

function buildCommonNameDescriptionFields(options: NameDescriptionOptions) {
  return {
    name: options.title,
    description: options.description,
  };
}

function handleWebSiteSchemaType(options: OrgLikeStructuredDataOptions) {
  const context = buildResolvedContext(options);
  const resolvedWebsiteName = options.websiteName || STRUCTURED_DATA_DEFAULTS.websiteName;
  const resolvedSiteOrigin = resolveSiteOrigin(context.canonicalUrl);
  const resolvedOrganizationId =
    options.organizationId ||
    STRUCTURED_DATA_DEFAULTS.organization.id ||
    (resolvedSiteOrigin ? `${resolvedSiteOrigin}/#organization` : undefined);
  return removeEmptyFields({
    ...buildSchemaBaseFields({
      context,
      schemaType: 'WebSite',
      idSuffix: 'website',
    }),
    name: resolvedWebsiteName,
    description: options.description,
    publisher: resolvedOrganizationId ? { '@id': resolvedOrganizationId } : undefined,
  });
}

function handleOrganizationSchemaType(options: OrgLikeStructuredDataOptions) {
  const context = buildResolvedContext(options);
  const resolvedSiteOrigin = resolveSiteOrigin(context.canonicalUrl);
  const resolvedOrganizationId =
    options.organizationId ||
    STRUCTURED_DATA_DEFAULTS.organization.id ||
    (resolvedSiteOrigin ? `${resolvedSiteOrigin}/#organization` : undefined);
  const resolvedOrganizationUrl =
    options.organizationUrl || STRUCTURED_DATA_DEFAULTS.organization.url || resolvedSiteOrigin;
  const resolvedOrganizationName =
    options.organizationName || STRUCTURED_DATA_DEFAULTS.organization.name;
  const resolvedOrganizationLegalName = options.organizationLegalName;
  const resolvedOrganizationLogoUrl = options.organizationLogoUrl;
  const resolvedSameAs = normalizeKeywords(
    options.sameAs || [...STRUCTURED_DATA_DEFAULTS.organization.sameAs]
  );
  const resolvedAddress = options.address;
  const resolvedFoundingDate = normalizeDateToISOString(options.foundingDate)?.split('T')[0];
  let resolvedParentOrganization: Record<string, string> | undefined;
  const resolvedParentOrganizationName =
    options.parentOrganizationName || STRUCTURED_DATA_DEFAULTS.organization.parentName;
  const resolvedParentOrganizationUrl =
    options.parentOrganizationUrl || STRUCTURED_DATA_DEFAULTS.organization.parentUrl;
  if (resolvedParentOrganizationName) {
    resolvedParentOrganization = {
      '@type': 'Organization',
      name: resolvedParentOrganizationName,
      url: resolvedParentOrganizationUrl || '',
    };
  }
  let resolvedContactPoints = [...STRUCTURED_DATA_DEFAULTS.organization.contactPoints];
  if (options.contactPoints?.length) {
    resolvedContactPoints = options.contactPoints;
  }
  let resolvedLogo: Record<string, unknown> | undefined;
  if (resolvedOrganizationLogoUrl) {
    resolvedLogo = {
      '@type': 'ImageObject',
      url: resolvedOrganizationLogoUrl,
      width: options.organizationLogoWidth,
      height: options.organizationLogoHeight,
    };
  }
  let resolvedPostalAddress: Record<string, unknown> | undefined;
  if (resolvedAddress) {
    resolvedPostalAddress = {
      '@type': 'PostalAddress',
      ...resolvedAddress,
    };
  }

  return removeEmptyFields({
    ...buildSchemaBaseFields({
      context,
      schemaType: 'Organization',
      idSuffix: 'organization',
      includeLanguage: false,
    }),
    '@id': resolvedOrganizationId,
    url: resolvedOrganizationUrl,
    name: resolvedOrganizationName,
    legalName: resolvedOrganizationLegalName,
    description: options.description,
    logo: resolvedLogo,
    foundingDate: resolvedFoundingDate,
    address: resolvedPostalAddress,
    parentOrganization: resolvedParentOrganization,
    sameAs: resolvedSameAs,
    contactPoint: resolvedContactPoints?.map((contactPoint) => ({
      '@type': 'ContactPoint',
      ...contactPoint,
    })),
  });
}

function handleArticleSchemaType(options: ArticleLikeStructuredDataOptions) {
  const context = buildResolvedContext(options);
  const resolvedSiteOrigin = resolveSiteOrigin(context.canonicalUrl);
  const resolvedOrganizationId =
    options.organizationId ||
    STRUCTURED_DATA_DEFAULTS.organization.id ||
    (resolvedSiteOrigin ? `${resolvedSiteOrigin}/#organization` : undefined);
  const resolvedOrganizationName =
    options.organizationName || STRUCTURED_DATA_DEFAULTS.organization.name;
  const resolvedOrganizationLogoUrl = options.organizationLogoUrl;
  const resolvedKeywords = normalizeKeywords(options.keywords);
  const resolvedAbout = normalizeKeywords(options.about);
  const resolvedMentions = normalizeKeywords(options.mentions);
  const datePublished = normalizeDateToISOString(options.datePublished);
  const dateModified = normalizeDateToISOString(options.dateModified);

  // Avoid dropping schema entirely when one date is missing by falling back to the other.
  const resolvedDatePublished = datePublished || dateModified;
  const resolvedDateModified = dateModified || datePublished;
  // Use organization as fallback author when person-level author data is unavailable.
  const resolvedAuthorName = options.authorName || resolvedOrganizationName;
  const resolvedPublisher: Record<string, unknown> = {
    '@type': 'Organization',
    name: resolvedOrganizationName,
  };
  if (resolvedOrganizationLogoUrl) {
    resolvedPublisher.logo = {
      '@type': 'ImageObject',
      url: resolvedOrganizationLogoUrl,
    };
  }
  let resolvedAuthor: Record<string, unknown> | undefined;
  if (resolvedAuthorName) {
    const resolvedAuthorType = options.authorName ? 'Person' : 'Organization';
    resolvedAuthor = {
      '@type': resolvedAuthorType,
      name: resolvedAuthorName,
    };

    if (options.authorName && options.authorUrl) {
      resolvedAuthor.url = options.authorUrl;
    }

    if (options.authorName && options.authorSameAs?.length) {
      resolvedAuthor.sameAs = options.authorSameAs;
    }
  }

  return removeEmptyFields({
    ...buildSchemaBaseFields({
      context,
      schemaType: options.schemaType,
      idSuffix: options.schemaType.toLowerCase(),
    }),
    headline: options.title,
    description: options.description,
    abstract: options.abstract,
    articleBody: options.contentSummary,
    datePublished: resolvedDatePublished,
    dateModified: resolvedDateModified,
    image: context.imageUrl ? [context.imageUrl] : undefined,
    articleSection: options.articleSection,
    wordCount: options.wordCount,
    isAccessibleForFree: options.isAccessibleForFree,
    about: resolvedAbout,
    mentions: resolvedMentions,
    keywords: resolvedKeywords,
    author: resolvedAuthor,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': context.canonicalUrl,
    },
    isPartOf: resolvedOrganizationId ? { '@id': resolvedOrganizationId } : undefined,
    publisher: resolvedPublisher,
  });
}

function handleBlogPostingSchemaType(options: ArticleLikeStructuredDataOptions) {
  return handleArticleSchemaType({ ...options, schemaType: 'BlogPosting' });
}

function handleNewsArticleSchemaType(options: ArticleLikeStructuredDataOptions) {
  return handleArticleSchemaType({ ...options, schemaType: 'NewsArticle' });
}

function handleTechArticleSchemaType(options: ArticleLikeStructuredDataOptions) {
  return handleArticleSchemaType({ ...options, schemaType: 'TechArticle' });
}

function handleCreativeWorkSchemaType(options: CreativeWorkStructuredDataOptions) {
  const context = buildResolvedContext(options);
  const resolvedKeywords = normalizeKeywords(options.keywords);

  return removeEmptyFields({
    ...buildSchemaBaseFields({
      context,
      schemaType: 'CreativeWork',
      idSuffix: 'creativework',
    }),
    ...buildCommonNameDescriptionFields(options),
    abstract: options.abstract,
    image: context.imageUrl ? [context.imageUrl] : undefined,
    keywords: resolvedKeywords,
  });
}

function handleEventSchemaType(options: EventStructuredDataOptions) {
  const context = buildResolvedContext(options);
  const resolvedStartDate = normalizeDateToISOString(options.startDate);
  const resolvedEndDate = normalizeDateToISOString(options.endDate);
  const offer = buildOfferSchemaType(options, context);
  let resolvedEventStatus = options.eventStatus;
  // Accept shorthand values (e.g., EventScheduled) and normalize to schema.org URLs.
  if (resolvedEventStatus && !resolvedEventStatus.startsWith('http')) {
    resolvedEventStatus = `https://schema.org/${resolvedEventStatus}`;
  }

  let resolvedEventAttendanceMode = options.eventAttendanceMode;
  // Same normalization pattern as eventStatus for consistency.
  if (resolvedEventAttendanceMode && !resolvedEventAttendanceMode.startsWith('http')) {
    resolvedEventAttendanceMode = `https://schema.org/${resolvedEventAttendanceMode}`;
  }

  let resolvedLocation: Record<string, string> | undefined;
  if (options.locationName || options.locationUrl) {
    resolvedLocation = {
      '@type': options.locationUrl ? 'VirtualLocation' : 'Place',
      name: options.locationName || '',
      url: options.locationUrl || '',
    };
  }

  let resolvedOrganizer: Record<string, string> | undefined;
  if (options.organizerName) {
    resolvedOrganizer = {
      '@type': 'Organization',
      name: options.organizerName,
      url: options.organizerUrl || '',
    };
  }

  return removeEmptyFields({
    ...buildSchemaBaseFields({
      context,
      schemaType: 'Event',
      idSuffix: options.idSuffix || 'event',
      includeLanguage: false,
    }),
    ...buildCommonNameDescriptionFields(options),
    startDate: resolvedStartDate,
    endDate: resolvedEndDate,
    eventStatus: resolvedEventStatus,
    eventAttendanceMode: resolvedEventAttendanceMode,
    location: resolvedLocation,
    organizer: resolvedOrganizer,
    offers: offer,
  });
}

function buildOfferSchemaType(
  options: CommerceLikeStructuredDataOptions | EventStructuredDataOptions,
  context: StructuredDataResolvedContext
) {
  // Emit Offer only when both price fields are present to avoid incomplete
  // commerce markup that commonly triggers rich-result warnings.
  const hasRequiredOfferPricing = Boolean(options.offerPrice && options.offerPriceCurrency);
  if (!hasRequiredOfferPricing) {
    return;
  }

  let offerIdSuffix = 'offer';
  if (options.idSuffix) {
    // Keep Offer IDs stable and unique when nested under Product/OfferCatalog/etc.
    offerIdSuffix = options.schemaType === 'Offer' ? options.idSuffix : `${options.idSuffix}-offer`;
  }

  let resolvedAvailability: string | undefined;
  if (options.offerAvailability) {
    if (options.offerAvailability.startsWith('http')) {
      resolvedAvailability = options.offerAvailability;
    } else {
      resolvedAvailability = `https://schema.org/${options.offerAvailability}`;
    }
  }
  const resolvedPriceValidUntil = normalizeDateToISOString(options.offerPriceValidUntil)?.split(
    'T'
  )[0];

  return removeEmptyFields({
    ...buildSchemaBaseFields({
      context,
      schemaType: 'Offer',
      idSuffix: offerIdSuffix,
      includeLanguage: false,
    }),
    ...buildCommonNameDescriptionFields(options),
    url: options.offerUrl || context.canonicalUrl,
    price: options.offerPrice,
    priceCurrency: options.offerPriceCurrency,
    availability: resolvedAvailability,
    priceValidUntil: resolvedPriceValidUntil,
    category: options.offerCategory,
  });
}

function handleOfferSchemaType(options: CommerceLikeStructuredDataOptions) {
  return buildOfferSchemaType(options, buildResolvedContext(options));
}

function handleOfferCatalogSchemaType(options: CommerceLikeStructuredDataOptions) {
  const context = buildResolvedContext(options);
  const offer = buildOfferSchemaType(options, context);

  return removeEmptyFields({
    ...buildSchemaBaseFields({
      context,
      schemaType: 'OfferCatalog',
      idSuffix: 'offercatalog',
      includeLanguage: false,
    }),
    ...buildCommonNameDescriptionFields(options),
    // OfferCatalog can expose multiple offers, but current input model emits at most one.
    itemListElement: offer ? [offer] : undefined,
  });
}

function handleProductSchemaType(options: CommerceLikeStructuredDataOptions) {
  const context = buildResolvedContext(options);
  const offer = buildOfferSchemaType(options, context);
  const resolvedReviews = options.reviews?.map((review) => {
    const resolvedReview: Record<string, unknown> = {
      '@type': 'Review',
      reviewBody: review.reviewBody,
      datePublished: normalizeDateToISOString(review.datePublished),
    };

    if (review.authorName) {
      resolvedReview.author = {
        '@type': 'Person',
        name: review.authorName,
      };
    }

    if (typeof review.reviewRatingValue === 'number') {
      resolvedReview.reviewRating = {
        '@type': 'Rating',
        ratingValue: review.reviewRatingValue,
      };
    }

    return resolvedReview;
  });

  let resolvedAggregateRating: Record<string, unknown> | undefined;
  if (options.aggregateRating) {
    resolvedAggregateRating = {
      '@type': 'AggregateRating',
      ...options.aggregateRating,
    };
  }

  return removeEmptyFields({
    ...buildSchemaBaseFields({
      context,
      schemaType: 'Product',
      idSuffix: options.idSuffix || 'product',
      includeLanguage: false,
    }),
    ...buildCommonNameDescriptionFields(options),
    image: context.imageUrl ? [context.imageUrl] : undefined,
    brand: options.brandName ? { '@type': 'Brand', name: options.brandName } : undefined,
    aggregateRating: resolvedAggregateRating,
    review: resolvedReviews,
    offers: offer,
  });
}

function handleSoftwareApplicationSchemaType(options: CommerceLikeStructuredDataOptions) {
  const context = buildResolvedContext(options);
  const offer = buildOfferSchemaType(options, context);
  let resolvedAggregateRating: Record<string, unknown> | undefined;
  if (options.aggregateRating) {
    resolvedAggregateRating = {
      '@type': 'AggregateRating',
      ...options.aggregateRating,
    };
  }

  return removeEmptyFields({
    ...buildSchemaBaseFields({
      context,
      schemaType: 'SoftwareApplication',
      idSuffix: 'softwareapplication',
    }),
    ...buildCommonNameDescriptionFields(options),
    abstract: options.abstract,
    image: context.imageUrl ? [context.imageUrl] : undefined,
    applicationCategory: options.applicationCategory,
    operatingSystem: options.operatingSystem,
    aggregateRating: resolvedAggregateRating,
    offers: offer,
  });
}

/** use page level structured data for SEO */
function structuredDataBuilder(options: StructuredDataOptions) {
  if (options.enabled === false) {
    // Keep per-entry opt-out lightweight for callers building mixed schema arrays.
    return;
  }

  const resolvedCanonicalUrl = resolveCanonicalUrl(options);
  if (!resolvedCanonicalUrl) {
    console.warn('Structured data skipped: canonical URL is required but could not be resolved.');
    return;
  }

  const resolvedOptions: StructuredDataOptions = {
    ...options,
    canonicalUrl: resolvedCanonicalUrl,
  };

  // Keep a single dispatch point so schema-specific handlers stay isolated and testable.
  switch (resolvedOptions.schemaType) {
    case 'WebSite':
      return handleWebSiteSchemaType(resolvedOptions);
    case 'Organization':
      return handleOrganizationSchemaType(resolvedOptions);
    case 'Event':
      return handleEventSchemaType(resolvedOptions);
    case 'BlogPosting':
      return handleBlogPostingSchemaType(resolvedOptions);
    case 'NewsArticle':
      return handleNewsArticleSchemaType(resolvedOptions);
    case 'SoftwareApplication':
      return handleSoftwareApplicationSchemaType(resolvedOptions);
    case 'Product':
      return handleProductSchemaType(resolvedOptions);
    case 'Offer':
      return handleOfferSchemaType(resolvedOptions);
    case 'OfferCatalog':
      return handleOfferCatalogSchemaType(resolvedOptions);
    case 'CreativeWork':
      return handleCreativeWorkSchemaType(resolvedOptions);
    case 'Article':
      return handleArticleSchemaType(resolvedOptions);
    case 'TechArticle':
      return handleTechArticleSchemaType(resolvedOptions);
  }
}

function getFeatureLabels(plan: NonNullable<PlanRefQueryResult>) {
  return plan.features
    ?.map((featureEntry) => featureEntry.feature?.prefLabel)
    .filter((featureLabel): featureLabel is string => Boolean(featureLabel));
}

function buildProductDescription(plan: NonNullable<PlanRefQueryResult>) {
  const featureLabels = getFeatureLabels(plan);

  return [
    plan.description || plan.tagline,
    featureLabels?.length ? `Features: ${featureLabels.join(', ')}` : undefined,
  ]
    .filter(Boolean)
    .join(' | ');
}

/** build structured data for a pricing plan */
export function buildPricingPlanStructuredData({
  plan,
  planRefId,
  defaultCurrency,
  resolvedPlanPrices,
}: {
  plan: NonNullable<PlanRefQueryResult>;
  planRefId: string;
  defaultCurrency: { value: string };
  resolvedPlanPrices?: Array<{ name?: string | null; value?: string | null }> | null;
}): StructuredDataOptions {
  // Price selection precedence:
  // 1) active currency variant, 2) default variant, 3) first available variant.
  const preferredCurrency = defaultCurrency?.value?.toLowerCase();
  const preferredPriceEntry = resolvedPlanPrices?.find(
    (price) => price.name?.toLowerCase() === preferredCurrency
  );
  const defaultPriceEntry = resolvedPlanPrices?.find((price) => price.name === 'default');
  const selectedPriceEntry = preferredPriceEntry ?? defaultPriceEntry ?? resolvedPlanPrices?.[0];
  const normalizedPriceText = (selectedPriceEntry?.value ?? '').replace(/,/g, '');
  const matchedPrice = normalizedPriceText.match(/\d+(?:\.\d+)?/);
  const variantCurrency = selectedPriceEntry?.name;
  // Variant names use currency-like keys (e.g., usd/gbp). If we fall back to
  // the default variant, use current app currency for Offer.priceCurrency.
  const resolvedCurrency =
    (variantCurrency && variantCurrency !== 'default' ? variantCurrency : preferredCurrency) ||
    undefined;

  return {
    schemaType: 'Product',
    title: plan.name,
    description: buildProductDescription(plan),
    offerPrice: matchedPrice?.[0],
    offerPriceCurrency: resolvedCurrency?.toUpperCase(),
    offerCategory: 'Subscription',
    idSuffix: `product-${planRefId}`,
    enabled: true,
  };
}

export default structuredDataBuilder;
